import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Layout from "../components/Layout";
import { api, fmtMoney, CURRENCIES, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { DemoLockNotice, DemoLockWrap } from "../components/DemoLock";

export default function Wallet() {
  const { t } = useTranslation();
  const tr = (k, o) => t(`wallet_page.${k}`, o);
  const [params, setParams] = useSearchParams();
  const [wallet, setWallet] = useState(null);
  const [amount, setAmount] = useState(100);
  const [cur, setCur] = useState("USD");
  const [busy, setBusy] = useState(false);
  const [polling, setPolling] = useState(null);

  const load = useCallback(async () => {
    const r = await api.get("/wallet");
    setWallet(r.data);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Poll on return from Stripe
  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) return;
    let attempts = 0;
    setPolling(tr("verifying_payment"));
    const tick = async () => {
      attempts++;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          setPolling(null);
          toast.success(`Deposit confirmed: ${fmtMoney(data.amount_total / 100, (data.currency || "usd").toUpperCase())}`);
          setParams({});
          load();
          return;
        }
        if (data.status === "expired" || attempts >= 8) {
          setPolling(null);
          toast.error("Payment not completed.");
          setParams({});
          return;
        }
        setTimeout(tick, 2000);
      } catch (e) {
        setPolling(null);
        toast.error(formatApiError(e.response?.data?.detail));
        setParams({});
      }
    };
    tick();
     
  }, []);

  useEffect(() => {
    if (params.get("cancelled")) {
      toast.error("Payment cancelled.");
      setParams({});
    }
     
  }, []);

  const deposit = async () => {
    if (cur !== "USD" && cur !== "EUR") {
      toast.error("Stripe deposits available in USD / EUR only.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        amount: Number(amount),
        currency: cur,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
      setBusy(false);
    }
  };

  return (
    <Layout>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-14 pb-10">
        <div className="label-tag mb-4">{tr("tag")}</div>
        <h1 className="font-serif text-5xl tracking-tight">{tr("title")}</h1>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-line border border-line">
            {CURRENCIES.map((c) => (
              <div key={c} className="bg-surface p-6" data-testid={`balance-${c}`}>
                <div className="label-tag">{c} {tr("balances")}</div>
                <div className="mono-num text-2xl mt-2">{fmtMoney(wallet?.balances?.[c] || 0, c)}</div>
              </div>
            ))}
          </div>
          <div className="border border-line mt-6 p-6 bg-surface flex items-center justify-between" data-testid="ucs-holdings">
            <div>
              <div className="label-tag">{tr("ucs_holdings")}</div>
              <div className="mono-num text-3xl mt-2">{(wallet?.ucs_tons || 0).toFixed(3)} <span className="text-sm text-ink2">tCO₂</span></div>
            </div>
            <div className="text-right">
              <div className="label-tag">{tr("estimated_value")}</div>
              <div className="mono-num text-xl text-ink2">≈ ${((wallet?.ucs_tons || 0) * 22).toFixed(2)}</div>
            </div>
          </div>
        </div>

        <aside className="lg:col-span-4">
          <div className="border border-ink p-7">
            <div className="label-tag">{tr("deposit_funds")}</div>
            <div className="font-serif text-2xl mt-1">{tr("add_to_wallet")}</div>
            <p className="text-xs text-ink2 mt-1">{t("wallet_page.deposit_hint", "Secure card payment via Stripe (USD / EUR).")}</p>

            <div className="mt-4"><DemoLockNotice action="deposit" /></div>
            <DemoLockWrap className="mt-6 space-y-4">
              <div>
                <label className="label-tag block mb-2">{tr("currency")}</label>
                <select className="input-field" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="deposit-currency">
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
              <div>
                <label className="label-tag block mb-2">{tr("amount")}</label>
                <input type="number" min="5" step="1" value={amount} onChange={(e) => setAmount(e.target.value)} className="input-field" data-testid="deposit-amount" />
              </div>
              <div className="flex gap-2">
                {[50, 100, 500, 1000].map((v) => (
                  <button key={v} onClick={() => setAmount(v)} className="px-3 py-1.5 text-[11px] font-mono border border-line hover:border-ink rounded-none" data-testid={`quick-${v}`}>
                    {v}
                  </button>
                ))}
              </div>
              <button onClick={deposit} disabled={busy} className="btn-primary w-full disabled:opacity-50" data-testid="deposit-submit">
                {busy ? tr("redirecting") : `${tr("deposit_via_stripe")} →`}
              </button>
              {polling && <div className="text-xs text-ink2" data-testid="payment-polling">{polling}</div>}
            </DemoLockWrap>
          </div>
        </aside>
      </section>
      <div className="h-24" />
    </Layout>
  );
}
